import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { graphql, withApollo } from 'react-apollo';
import compose from 'lodash/flowRight';
import Raven from 'raven-js';

import WithAuthService from '@/hocs/withAuthService';
import { useAuthService } from '@/contexts/authService';
import { trackEventSaved, trackEventUnsaved } from '../../utils/tracking';
import { GQLQueryPropType, eventFullPropTypeShape } from '../../proptypes';
import {
  SaveEventMutation,
  getSaveEventMutationOptimisticResponse,
  UnsaveEventMutation,
  getUnsaveEventMutationOptimisticResponse,
  UserQueryPublicSavedEvents,
} from '../../state/services/gql/user';
import { logCustomEvent, BrazeCustomEvents } from '../../state/services/braze';

import HeartButton from '../../components/HeartButton';

class HeartContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loggedIn: props.authService.loggedIn(),
      tooltipVisible: false,
    };
  }

  setTooltip = (isVisible) => {
    this.setState({
      tooltipVisible: isVisible,
    });
  };

  isSaved = () => {
    const { data } = this.props;
    const savedEvents = data && data.profile && data.profile.savedEvents;
    if (savedEvents) {
      return savedEvents
        .map((savedEvent) => savedEvent.event.id)
        .includes(this.props.id.toString());
    }
    return false;
  };

  handleClick = ({ target }) => {
    if (this.isSaved()) {
      this.unsaveEvent(target.id.toString());
    } else {
      this.saveEvent(target.id.toString());
    }
  };

  handleSaveEventMouseEnter = () => {
    if (!this.props.noTooltip) {
      this.setTooltip(true);
    }
  };

  handleSaveEventMouseLeave = () => {
    if (!this.props.noTooltip) {
      this.setTooltip(false);
    }
  };

  saveEvent(id) {
    this.props
      .onSave({
        variables: {
          id,
        },
        optimisticResponse: getSaveEventMutationOptimisticResponse(
          this.props.client,
          id,
          this.props.data.profile,
        ),
      })
      .then(() => {
        trackEventSaved(this.props.trackingObject);
        logCustomEvent(
          BrazeCustomEvents.EVENT_SAVED,
          this.props.trackingObject,
        );
      })
      .catch((err) => {
        Raven.captureException(err);
      });
  }

  unsaveEvent(id) {
    this.props
      .onUnsave({
        variables: {
          id,
        },
        optimisticResponse: getUnsaveEventMutationOptimisticResponse(
          id,
          this.props.data.profile,
        ),
      })
      .then(() => {
        trackEventUnsaved(this.props.trackingObject);
        logCustomEvent(
          BrazeCustomEvents.EVENT_UNSAVED,
          this.props.trackingObject,
        );
      })
      .catch((err) => {
        Raven.captureException(err);
      });
  }

  render() {
    const { children } = this.props;
    return (
      <HeartButton
        onClick={this.handleClick}
        onMouseEnter={this.handleSaveEventMouseEnter}
        onMouseLeave={this.handleSaveEventMouseLeave}
        {...this.props}
        {...this.state}
        saved={this.isSaved()}
      >
        {children}
      </HeartButton>
    );
  }
}

HeartContainer.propTypes = {
  /**
   * Boolean whether booking is enabled.
   */
  bookingPartner: PropTypes.shape(),
  /**
   * Children contents of button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  /**
   * The amount of time the event has been saved.
   */
  count: PropTypes.number,
  /**
   * Data object returned from GraphQL query.
   */
  data: GQLQueryPropType,
  /**
   * Boolean used in order to not render tooltip on hovering over button.
   */
  noTooltip: PropTypes.bool,
  /**
   * Callback function when user clicks on Save Event button.
   */
  onSave: PropTypes.func,
  /**
   * Callback function when user unsaves event.
   */
  onUnsave: PropTypes.func,
  /**
   * String to return the user to after login wall
   */
  loginReturnUrl: PropTypes.string,
  /**
   * Event object to be tracked on save/unsave using Segment.
   */
  trackingObject: PropTypes.shape({
    id: eventFullPropTypeShape.id,
    title: eventFullPropTypeShape.title,
    tags: eventFullPropTypeShape.notifications,
    organiser: eventFullPropTypeShape.organiser.title,
    startDate: eventFullPropTypeShape.startDate,
    bookingEnabled: eventFullPropTypeShape.bookingEnabled,
  }).isRequired,
};

HeartContainer.defaultProps = {
  bookingPartner: undefined,
  loginReturnUrl: undefined,
  count: 0,
  data: {},
  noTooltip: undefined,
  onSave: () => {},
  onUnsave: () => {},
};

// Shorthand object definition, maps action creator to dispatch
export default (props) => {
  const authService = useAuthService();
  const HeartContainerWithApollo = withApollo(HeartContainer);

  // GraphQL mutations and query are only needed when logged in.
  // TODO: This should be another layer of abstraction/container.
  const HeartContainerWithGraphQL = compose(
    graphql(SaveEventMutation, { name: 'onSave' }),
    graphql(UnsaveEventMutation, { name: 'onUnsave' }),
    graphql(UserQueryPublicSavedEvents, {
      options: { variables: { id: authService.getUserCookie() } },
      skip: !authService.getUserCookie(),
    }),
  )(WithAuthService(HeartContainerWithApollo));

  return <HeartContainerWithGraphQL {...props} />;
};
